/**
 * API Client for UI3 Demo
 *
 * Handles all API requests with Firebase authentication.
 * Distributed tracing handled by Sentry auto-instrumentation.
 */

import { auth } from './firebase';
import { axiom } from './axiom';

import type {
  Stream,
  StreamWithGraph,
  Node,
  Edge,
  ChatMessage,
  UsageData,
  UsageLimits,
  CreateStreamInput,
  UpdateStreamInput,
  CreateNodeInput,
  UpdateNodeInput,
  CreateEdgeInput,
  AttentionNode,
} from '../types/api';
import type { ReviewSummary, ReviewDetail, RoundEntry, ReviewContextData, ReviewDebugData } from '../components/reviews/types';
import type { BudgetStateData } from '../components/reviews/CostBanner';

/** GET /costs/history response (Spec 199 Phase 5). One row per
 *  (date, provider, pool, source) — chart consumer groups by provider
 *  and stacks by pool. `reconciliation` is a Gemini-only delta between
 *  internal Axiom telemetry and GCP BQ billing; currently null
 *  until the api worker gets a BQ binding (see PR-A header note). */
export interface CostsHistoryBucket {
  date: string;       // YYYY-MM-DD
  provider: string;
  pool: string;
  source: string;
  dailyUsd: number;
}

export interface CostsHistoryReconciliation {
  internalMtdUsd: number;
  gcpBqMtdUsd: number;
  deltaUsd: number;
  driftPercent: number;
  warn: boolean;
}

export interface CostsHistoryResponse {
  days: number;
  buckets: CostsHistoryBucket[];
  reconciliation: CostsHistoryReconciliation | null;
}

export interface CfAccessServiceToken {
  clientId: string;
  clientSecret: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Build a query string for budget routes from an optional `pool` + arbitrary
 * extra params, encoding via URLSearchParams (which handles
 * percent-encoding identically to `encodeURIComponent` for the values we
 * pass). Empty pool + empty extras → empty string.
 */
function budgetQs(pool: string | undefined, extras?: Record<string, string>): string {
  const params = new URLSearchParams();
  if (extras) {
    for (const [key, value] of Object.entries(extras)) {
      params.set(key, value);
    }
  }
  if (pool) params.set('pool', pool);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

class ApiClient {
  private baseUrl = import.meta.env.VITE_API_URL || '/api';
  private testMode = false;
  private testEmail: string | null = null;
  private cfAccessServiceToken: CfAccessServiceToken | null = null;

  /**
   * Enable test mode - bypasses Firebase auth and uses X-Test-User-Email header.
   * Only works when backend is running with DEV_MODE=true.
   * @see docs/specs/044-integration-testing-framework.md
   */
  setTestMode(email: string): void {
    this.testMode = true;
    this.testEmail = email;
    this.cfAccessServiceToken = null;
  }

  /**
   * Disable test mode - restore Firebase auth.
   */
  clearTestMode(): void {
    this.testMode = false;
    this.testEmail = null;
  }

  /**
   * Use a CF Access service token for production E2E tests.
   * Cloudflare validates the pair at the edge and injects the JWT consumed by
   * the API's allowlisted CF Access auth branch.
   * @see docs/specs/044-integration-testing-framework.md Production Auth
   */
  setCfAccessServiceToken(token: CfAccessServiceToken): void {
    this.cfAccessServiceToken = { ...token };
    this.testMode = false;
    this.testEmail = null;
  }

  /**
   * Disable CF Access E2E mode - restore Firebase auth.
   */
  clearCfAccessServiceToken(): void {
    this.cfAccessServiceToken = null;
  }

  /**
   * Get Firebase ID token for authentication
   */
  private async getIdToken(): Promise<string | null> {
    const user = auth?.currentUser;
    if (!user) return null;
    try {
      return await user.getIdToken();
    } catch (error) {
      axiom.error('Failed to get ID token', { error: error instanceof Error ? error.message : String(error) });
      return null;
    }
  }

  /**
   * Make authenticated API request with distributed tracing
   * Sentry auto-instruments fetch() with sentry-trace and baggage headers
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // CF Access service-token mode - for production E2E tests
    if (this.cfAccessServiceToken) {
      if (import.meta.env.DEV) {
        console.debug(`[API] ${options.method || 'GET'} ${endpoint} (CF Access E2E mode)`);
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'CF-Access-Client-Id': this.cfAccessServiceToken.clientId,
          'CF-Access-Client-Secret': this.cfAccessServiceToken.clientSecret,
          ...options.headers,
        },
      });

      return this.handleResponse<T>(response);
    }

    // Test mode - for local integration tests with DEV_MODE=true
    if (this.testMode && this.testEmail) {
      if (import.meta.env.DEV) {
        console.debug(`[API] ${options.method || 'GET'} ${endpoint} (test mode)`);
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'X-Test-User-Email': this.testEmail,
          ...options.headers,
        },
      });

      return this.handleResponse<T>(response);
    }

    const token = await this.getIdToken();

    if (!token) {
      throw new ApiError(401, 'Not authenticated');
    }

    // Log request for debugging (Sentry handles trace propagation)
    if (import.meta.env.DEV) {
      console.debug(`[API] ${options.method || 'GET'} ${endpoint}`);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Handle API response - extract JSON and throw on error
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      if (!response.ok) {
        throw new ApiError(response.status, 'Request failed');
      }
      return {} as T;
    }

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        response.status,
        data.message || data.error || 'Request failed',
        data.error
      );
    }

    return data as T;
  }

  // ============================================
  // STREAMS
  // ============================================

  streams = {
    /**
     * List all streams for current user
     */
    list: (): Promise<Stream[]> => {
      return this.request<Stream[]>('/streams');
    },

    /**
     * Get single stream with nodes and edges
     */
    get: (id: string): Promise<StreamWithGraph> => {
      return this.request<StreamWithGraph>(`/streams/${id}`);
    },

    /**
     * Create new stream
     */
    create: (data: CreateStreamInput): Promise<Stream> => {
      return this.request<Stream>('/streams', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update stream
     */
    update: (id: string, data: UpdateStreamInput): Promise<Stream> => {
      return this.request<Stream>(`/streams/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete (archive) stream
     */
    delete: (id: string): Promise<void> => {
      return this.request<void>(`/streams/${id}`, {
        method: 'DELETE',
      });
    },
  };

  // ============================================
  // NODES
  // ============================================

  nodes = {
    /**
     * List nodes in a stream
     */
    list: (streamId: string): Promise<Node[]> => {
      return this.request<Node[]>(`/streams/${streamId}/nodes`);
    },

    /**
     * Create node in a stream
     */
    create: (streamId: string, data: CreateNodeInput): Promise<Node> => {
      return this.request<Node>(`/streams/${streamId}/nodes`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update node
     */
    update: (id: string, data: UpdateNodeInput): Promise<Node> => {
      return this.request<Node>(`/nodes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete node
     */
    delete: (id: string): Promise<void> => {
      return this.request<void>(`/nodes/${id}`, {
        method: 'DELETE',
      });
    },
  };

  // ============================================
  // EDGES
  // ============================================

  edges = {
    /**
     * List edges in a stream
     */
    list: (streamId: string): Promise<Edge[]> => {
      return this.request<Edge[]>(`/streams/${streamId}/edges`);
    },

    /**
     * Create edge in a stream
     */
    create: (streamId: string, data: CreateEdgeInput): Promise<Edge> => {
      return this.request<Edge>(`/streams/${streamId}/edges`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete edge
     */
    delete: (id: number): Promise<void> => {
      return this.request<void>(`/edges/${id}`, {
        method: 'DELETE',
      });
    },
  };

  // ============================================
  // CHAT
  // ============================================

  chat = {
    /**
     * Get chat history for a stream (optionally filtered by node)
     */
    history: (streamId: string, nodeId?: string): Promise<ChatMessage[]> => {
      const params = nodeId ? `?node_id=${nodeId}` : '';
      return this.request<ChatMessage[]>(`/streams/${streamId}/chat${params}`);
    },
  };

  // ============================================
  // USAGE
  // ============================================

  usage = {
    /**
     * Get current usage stats
     */
    stats: (): Promise<UsageData> => {
      return this.request<UsageData>('/usage');
    },

    /**
     * Get usage limits
     */
    limits: (): Promise<UsageLimits> => {
      return this.request<UsageLimits>('/usage/limits');
    },
  };

  // ============================================
  // ATTENTION
  // ============================================

  attention = {
    /**
     * List all nodes requiring attention across user's streams
     */
    list: (): Promise<AttentionNode[]> => {
      return this.request<AttentionNode[]>('/attention');
    },
  };

  // ============================================
  // REVIEWS (Spec 127: Review Command Centre)
  // ============================================

  reviews = {
    list: (): Promise<{ reviews: ReviewSummary[] }> => {
      return this.request<{ reviews: ReviewSummary[] }>('/reviews');
    },

    get: (sessionId: string): Promise<ReviewDetail> => {
      return this.request<ReviewDetail>(`/reviews/${sessionId}`);
    },

    rounds: (sessionId: string): Promise<{ rounds: RoundEntry[] }> => {
      return this.request<{ rounds: RoundEntry[] }>(`/reviews/${sessionId}/rounds`);
    },

    context: (sessionId: string, opts?: { includePrompt?: boolean; epoch?: number }): Promise<ReviewContextData> => {
      const params = new URLSearchParams();
      if (opts?.includePrompt) params.set('include', 'prompt');
      if (opts?.epoch !== undefined) params.set('epoch', String(opts.epoch));
      const qs = params.toString();
      return this.request<ReviewContextData>(`/reviews/${sessionId}/context${qs ? `?${qs}` : ''}`);
    },

    /** Spec 188 Phase 4 — step timeline + reviewer outputs + cost-by-round. */
    debug: (sessionId: string, opts?: { epoch?: number }): Promise<ReviewDebugData> => {
      const qs = opts?.epoch !== undefined ? `?epoch=${opts.epoch}` : '';
      return this.request<ReviewDebugData>(`/reviews/${sessionId}/debug${qs}`);
    },

    /** Spec 188 Phase 1B — retry a failed workflow from a given free step. */
    retry: (sessionId: string, fromStep: string): Promise<{ workflowId: string; status: string; fromStep: string }> => {
      return this.request<{ workflowId: string; status: string; fromStep: string }>(
        `/reviews/${sessionId}/retry`,
        { method: 'POST', body: JSON.stringify({ fromStep }) },
      );
    },

    /** Spec 206 Phase 4 — review service toggle config. */
    getConfig: (): Promise<{ enabled: boolean; reason?: string; disabledBy?: string; disabledAt?: string }> => {
      return this.request<{ enabled: boolean; reason?: string; disabledBy?: string; disabledAt?: string }>('/reviews/config');
    },

    setConfig: (config: { enabled: boolean; reason?: string }): Promise<{ enabled: boolean; reason?: string; disabledBy?: string; disabledAt?: string }> => {
      return this.request<{ enabled: boolean; reason?: string; disabledBy?: string; disabledAt?: string }>(
        '/reviews/config',
        { method: 'PUT', body: JSON.stringify(config) },
      );
    },
  };

  // ============================================
  // BUDGET (Spec 188 Phase 2B)
  // ============================================

  budget = {
    get: (pool?: string): Promise<BudgetStateData> => {
      return this.request<BudgetStateData>(`/budget${budgetQs(pool)}`);
    },
    getPools: (): Promise<Record<string, BudgetStateData>> => {
      return this.request<Record<string, BudgetStateData>>('/budget/pools');
    },
    updateLimits: (
      input: { dailyLimitUsd: number; monthlyLimitUsd: number },
      pool?: string
    ): Promise<BudgetStateData> => {
      return this.request<BudgetStateData>(`/budget/limits${budgetQs(pool)}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      });
    },
    reset: (scope: 'daily' | 'monthly' = 'daily', pool?: string): Promise<BudgetStateData> => {
      const qs = budgetQs(pool, scope === 'monthly' ? { scope: 'monthly' } : undefined);
      return this.request<BudgetStateData>(`/budget/reset${qs}`, { method: 'POST' });
    },
    override: (reason: string, pool?: string): Promise<BudgetStateData> => {
      return this.request<BudgetStateData>(`/budget/override${budgetQs(pool)}`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    },
  };

  // ============================================
  // COSTS HISTORY (Spec 199 Phase 5)
  // ============================================

  costs = {
    /** 30-day daily AI provider cost history backing the trend charts on /costs.
     *  Server-side 5-min KV cache; admin-gated. */
    getHistory: (days: number = 30): Promise<CostsHistoryResponse> => {
      return this.request<CostsHistoryResponse>(`/costs/history?days=${days}`);
    },
  };

  // ============================================
  // WEBSOCKET
  // ============================================

  websocket = {
    /**
     * Get a WebSocket connection ticket for the user session.
     * Used by UserWebSocket to establish Hub-and-Spoke connection.
     */
    getUserTicket: (): Promise<{ ticket: string; expires_in: number; websocket_url: string }> => {
      return this.request<{ ticket: string; expires_in: number; websocket_url: string }>(
        '/ws/user/ticket',
        { method: 'POST' }
      );
    },
  };
}

// Export singleton instance
export const api = new ApiClient();
